import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, Chip, Checkbox,
  CircularProgress, IconButton, InputAdornment, Divider,
  FormControl, Select, MenuItem, LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SearchIcon from '@mui/icons-material/Search';
import { getStigLibrary } from '../api/stigMgmt';
import { getAssets } from '../api/assets';
import { bulkApplyStig } from '../api/stigMgmt';

const FIELD_SX = {
  '& .MuiInputBase-root': { backgroundColor: '#0d1825', color: '#c0cfe0', fontSize: 13 },
  '& fieldset': { borderColor: '#1e3a5f' },
  '& .MuiInputLabel-root': { color: '#7a8fa6' },
};

const TYPE_LABELS = {
  workstation: 'Workstation', server: 'Server', firewall: 'Firewall',
  router: 'Router', switch: 'Switch', hypervisor: 'Hypervisor',
  vm: 'VM', taclane: 'TACLANE', idps: 'IDPS', vuln_scanner: 'Vuln Scanner', other: 'Other',
};

export default function BulkStigAssignDialog({ open, onClose, systemId }) {
  const [step, setStep]               = useState(0); // 0=pick stig, 1=pick devices, 2=results
  const [library, setLibrary]         = useState([]);
  const [assets, setAssets]           = useState([]);
  const [stigSearch, setStigSearch]   = useState('');
  const [deviceSearch, setDeviceSearch] = useState('');
  const [filterOS, setFilterOS]       = useState('');
  const [filterType, setFilterType]   = useState('');
  const [selectedStig, setSelectedStig] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [applying, setApplying]       = useState(false);
  const [results, setResults]         = useState(null);

  useEffect(() => {
    if (open) {
      getStigLibrary(systemId).then(setLibrary).catch(console.error);
      getAssets(systemId).then(setAssets).catch(console.error);
      setStep(0);
      setSelectedStig(null);
      setSelectedIds(new Set());
      setResults(null);
      setStigSearch('');
      setDeviceSearch('');
      setFilterOS('');
      setFilterType('');
    }
  }, [open]);

  const filteredStigs = library.filter(s =>
    s.title.toLowerCase().includes(stigSearch.toLowerCase()) ||
    s.filename.toLowerCase().includes(stigSearch.toLowerCase())
  );

  const osOptions   = [...new Set(assets.map(a => a.os).filter(Boolean))].sort();
  const typeOptions = [...new Set(assets.map(a => a.device_type).filter(Boolean))].sort();

  const filteredAssets = assets.filter(a => {
    if (filterOS   && a.os !== filterOS) return false;
    if (filterType && a.device_type !== filterType) return false;
    if (deviceSearch && !a.hostname?.toLowerCase().includes(deviceSearch.toLowerCase()) &&
        !a.ip_address?.includes(deviceSearch)) return false;
    return true;
  });

  const toggleAll = () => {
    if (selectedIds.size === filteredAssets.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredAssets.map(a => a.id)));
    }
  };

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectedAssets = assets.filter(a => selectedIds.has(a.id));

  const handleApply = async () => {
    setApplying(true);
    try {
      const res = await bulkApplyStig(systemId, selectedStig.filename, [...selectedIds]);
      setResults(res);
      setStep(2);
    } catch (e) {
      setResults({ error: e.message });
      setStep(2);
    }
    setApplying(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f', height: '80vh', display: 'flex', flexDirection: 'column' } }}>

      <DialogTitle sx={{ color: '#ffffff', borderBottom: '1px solid #1e3a5f', display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
        <Box>
          <Typography sx={{ color: '#ffffff', fontWeight: 700 }}>Bulk STIG Assignment</Typography>
          <Typography sx={{ color: '#7a8fa6', fontSize: 12 }}>
            {step === 0 ? 'Step 1 of 2 — Select STIG' : step === 1 ? 'Step 2 of 2 — Select Devices' : 'Results'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#7a8fa6' }}><CloseIcon /></IconButton>
      </DialogTitle>

      {/* Progress bar */}
      {step < 2 && (
        <LinearProgress variant="determinate" value={step === 0 ? 25 : 75}
          sx={{ backgroundColor: '#1e3a5f', '& .MuiLinearProgress-bar': { backgroundColor: '#00bcd4' } }} />
      )}

      <DialogContent sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 2, gap: 1.5 }}>

        {/* STEP 0: Pick STIG */}
        {step === 0 && (
          <>
            <TextField fullWidth size="small" placeholder="Search STIGs..." value={stigSearch}
              onChange={e => setStigSearch(e.target.value)} sx={FIELD_SX}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#7a8fa6', fontSize: 18 }} /></InputAdornment> }} />
            <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {filteredStigs.map(s => {
                const selected = selectedStig?.filename === s.filename;
                return (
                  <Box key={s.filename} onClick={() => setSelectedStig(s)}
                    sx={{ p: '10px 14px', borderRadius: 1, border: `1px solid ${selected ? '#00bcd4' : '#1e3a5f'}`,
                      backgroundColor: selected ? '#0d2137' : '#0d1825', cursor: 'pointer',
                      '&:hover': { border: '1px solid #00bcd4', backgroundColor: '#0d2137' } }}>
                    <Typography sx={{ color: '#ffffff', fontSize: 13, fontWeight: 600 }}>{s.title}</Typography>
                    <Typography sx={{ color: '#7a8fa6', fontSize: 11 }}>V{s.version}R{s.release} · {s.filename}</Typography>
                  </Box>
                );
              })}
              {filteredStigs.length === 0 && (
                <Typography sx={{ color: '#4a5a70', fontSize: 13, textAlign: 'center', mt: 4 }}>
                  No STIGs found in library
                </Typography>
              )}
            </Box>
          </>
        )}

        {/* STEP 1: Pick Devices */}
        {step === 1 && (
          <>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField size="small" placeholder="Search hostname / IP..." value={deviceSearch}
                onChange={e => setDeviceSearch(e.target.value)} sx={{ ...FIELD_SX, flex: 1 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#7a8fa6', fontSize: 18 }} /></InputAdornment> }} />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select value={filterOS} onChange={e => setFilterOS(e.target.value)} displayEmpty
                  sx={{ backgroundColor: '#0d1825', color: '#c0cfe0', fontSize: 13, '.MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' } }}>
                  <MenuItem value="">All OS</MenuItem>
                  {osOptions.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select value={filterType} onChange={e => setFilterType(e.target.value)} displayEmpty
                  sx={{ backgroundColor: '#0d1825', color: '#c0cfe0', fontSize: 13, '.MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' } }}>
                  <MenuItem value="">All Types</MenuItem>
                  {typeOptions.map(t => <MenuItem key={t} value={t}>{TYPE_LABELS[t] || t}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>

            {/* Select all / count */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }} onClick={toggleAll}>
                <Checkbox
                  checked={selectedIds.size === filteredAssets.length && filteredAssets.length > 0}
                  indeterminate={selectedIds.size > 0 && selectedIds.size < filteredAssets.length}
                  size="small"
                  sx={{ color: '#1e3a5f', '&.Mui-checked': { color: '#00bcd4' }, '&.MuiCheckbox-indeterminate': { color: '#00bcd4' }, p: 0 }}
                />
                <Typography sx={{ color: '#7a8fa6', fontSize: 12 }}>Select all ({filteredAssets.length})</Typography>
              </Box>
              {selectedIds.size > 0 && (
                <Typography sx={{ color: '#00bcd4', fontSize: 12, fontWeight: 700 }}>{selectedIds.size} selected</Typography>
              )}
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {filteredAssets.map(a => {
                const checked = selectedIds.has(a.id);
                return (
                  <Box key={a.id} onClick={() => toggleOne(a.id)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '8px 12px', borderRadius: 1,
                      border: `1px solid ${checked ? '#00bcd4' : '#1e3a5f'}`,
                      backgroundColor: checked ? '#0d2137' : '#0d1825', cursor: 'pointer',
                      '&:hover': { border: '1px solid #00bcd4' } }}>
                    <Checkbox checked={checked} size="small"
                      sx={{ color: '#1e3a5f', '&.Mui-checked': { color: '#00bcd4' }, p: 0 }} />
                    <Typography sx={{ color: '#ffffff', fontFamily: 'monospace', fontSize: 13, fontWeight: 600, minWidth: 140 }}>{a.hostname}</Typography>
                    <Typography sx={{ color: '#7a8fa6', fontSize: 12, minWidth: 110 }}>{a.ip_address}</Typography>
                    <Typography sx={{ color: '#7a8fa6', fontSize: 12, flex: 1 }}>{a.os}</Typography>
                    <Chip label={TYPE_LABELS[a.device_type] || a.device_type || 'Unknown'} size="small"
                      sx={{ backgroundColor: '#1e3a5f', color: '#c0cfe0', fontSize: 10, height: 20 }} />
                  </Box>
                );
              })}
              {filteredAssets.length === 0 && (
                <Typography sx={{ color: '#4a5a70', fontSize: 13, textAlign: 'center', mt: 4 }}>
                  No devices match the current filters
                </Typography>
              )}
            </Box>
          </>
        )}

        {/* STEP 2: Results */}
        {step === 2 && results && (
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {results.error ? (
              <Typography sx={{ color: '#f44336', p: 2 }}>Error: {results.error}</Typography>
            ) : (
              <>
                <Box sx={{ mb: 2, p: '12px 16px', backgroundColor: '#0d1825', borderRadius: 1, border: '1px solid #1e3a5f' }}>
                  <Typography sx={{ color: '#ffffff', fontWeight: 700 }}>{results.stig_id}</Typography>
                  <Typography sx={{ color: '#7a8fa6', fontSize: 12 }}>Applied to {results.results?.length} device{results.results?.length !== 1 ? 's' : ''}</Typography>
                </Box>
                {results.results?.map(r => (
                  <Box key={r.asset_id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '8px 12px',
                    borderBottom: '1px solid #1e3a5f' }}>
                    {r.status === 'ok'
                      ? <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 18 }} />
                      : <ErrorIcon sx={{ color: '#f44336', fontSize: 18 }} />}
                    <Typography sx={{ color: '#ffffff', fontFamily: 'monospace', fontSize: 13, minWidth: 140 }}>{r.hostname || `Asset ${r.asset_id}`}</Typography>
                    {r.status === 'ok' ? (
                      <Typography sx={{ color: '#7a8fa6', fontSize: 12 }}>
                        {r.new_checks} new checks added · {r.total_rules} total rules
                      </Typography>
                    ) : (
                      <Typography sx={{ color: '#f44336', fontSize: 12 }}>{r.message}</Typography>
                    )}
                  </Box>
                ))}
              </>
            )}
          </Box>
        )}

      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #1e3a5f', p: 1.5, gap: 1 }}>
        {step === 0 && (
          <>
            <Button onClick={onClose} sx={{ color: '#7a8fa6' }}>Cancel</Button>
            <Button variant="contained" onClick={() => setStep(1)} disabled={!selectedStig}
              sx={{ backgroundColor: '#00bcd4', color: '#0a0e1a', fontWeight: 700 }}>
              Next — Select Devices
            </Button>
          </>
        )}
        {step === 1 && (
          <>
            <Button onClick={() => setStep(0)} sx={{ color: '#7a8fa6' }}>Back</Button>
            <Box sx={{ flex: 1 }} />
            {selectedIds.size > 0 && (
              <Typography sx={{ color: '#7a8fa6', fontSize: 12, alignSelf: 'center' }}>
                {selectedStig?.title} → {selectedIds.size} device{selectedIds.size !== 1 ? 's' : ''}
              </Typography>
            )}
            <Button variant="contained" onClick={handleApply} disabled={selectedIds.size === 0 || applying}
              sx={{ backgroundColor: '#00bcd4', color: '#0a0e1a', fontWeight: 700 }}>
              {applying ? <CircularProgress size={18} sx={{ color: '#0a0e1a' }} /> : `Apply to ${selectedIds.size} Device${selectedIds.size !== 1 ? 's' : ''}`}
            </Button>
          </>
        )}
        {step === 2 && (
          <Button variant="contained" onClick={onClose}
            sx={{ backgroundColor: '#00bcd4', color: '#0a0e1a', fontWeight: 700 }}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
